import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback: (error: Error, errorInfo: ErrorInfo | null, reset: () => void) => ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary class component that catches unhandled rendering errors
 * and displays fallback UI instead of crashing the entire app.
 * 
 * React 17 requires class components for Error Boundaries - no functional alternative exists.
 * 
 * Usage:
 * <ErrorBoundary fallback={(error, errorInfo, reset) => <ErrorFallback error={error} errorInfo={errorInfo} onReset={reset} />}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null,
    };

    /**
     * Update state when error is caught to trigger fallback UI render
     */
    public static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    /**
     * Log error details and store errorInfo for debugging
     * Phase 4 will integrate Sentry logging here
     */
    public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error('ErrorBoundary caught error:', error, errorInfo);
        this.setState({ errorInfo });
    }

    /**
     * Reset error state to attempt re-rendering the component tree
     */
    private handleReset = (): void => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    public render(): ReactNode {
        if (this.state.hasError && this.state.error) {
            return this.props.fallback(this.state.error, this.state.errorInfo, this.handleReset);
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
